import { useEffect, useMemo, useState } from 'react';
import { Layout, Typography, Drawer, ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import {
  FileTextOutlined,
  PictureOutlined,
  TeamOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { can } from '../auth/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import SiderNav from './SiderNav';
import NavToggle from './NavToggle';
import type { NavItem } from './SiderNav';
import { HeaderSlotContext } from './header-slot';
import type { Me } from '../api/client';

const { Sider, Header, Content } = Layout;

// 與 main.tsx 的 Layout.siderBg 一致：手機抽屜沿用側欄深色底
const SIDER_BG = '#1f2733';
// 手機：輸入欄位字級 ≥16px，避免 iOS focus 時自動放大
const MOBILE_THEME: ThemeConfig = {
  components: { Input: { inputFontSize: 16 }, Select: { fontSize: 16 }, DatePicker: { inputFontSize: 16 } },
};

interface Props {
  me: Me;
  children: React.ReactNode;
}

/** header 標題：選單項目名稱；不在選單裡的頁面（我的帳號）另外對應。 */
function headerTitle(items: { key: string; label: string }[], selected: string): string {
  return items.find((i) => i.key === selected)?.label ?? (selected === '/account' ? '我的帳號' : '後台');
}

/** 後台外殼：深色側欄（含左下使用者選單）+ 可插入內容的 header；手機改為漢堡選單 + 左側抽屜。 */
export default function AppLayout({ me, children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sider-collapsed') === '1');
  const [navOpen, setNavOpen] = useState(false);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
  const [slotActive, setSlotActive] = useState(false);

  // 換頁即關閉手機抽屜（含從使用者選單進「我的帳號」）
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // 只在桌機手動收折時記住偏好；手機不寫 localStorage
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sider-collapsed', next ? '1' : '0');
  };

  const slotValue = useMemo(() => ({ slotEl, setActive: setSlotActive }), [slotEl]);

  const items = [
    { key: '/articles', icon: <FileTextOutlined />, label: '文章' },
    { key: '/media', icon: <PictureOutlined />, label: '媒體庫' },
    can(me, 'user.manage') && { key: '/users', icon: <TeamOutlined />, label: '帳號管理' },
    can(me, 'settings.manage') && { key: '/settings', icon: <SettingOutlined />, label: '設定' },
  ].filter(Boolean) as NavItem[];

  const selected = '/' + (location.pathname.split('/')[1] || 'articles');
  const go = (key: string) => {
    setNavOpen(false);
    navigate(key);
  };

  return (
    <HeaderSlotContext.Provider value={slotValue}>
      <Layout className="h-[100dvh]">
        {!isMobile && (
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            breakpoint="lg"
            onBreakpoint={(broken) => broken && setCollapsed(true)}
            collapsedWidth={64}
            width={216}
            className="h-[100dvh]"
          >
            <SiderNav me={me} collapsed={collapsed} items={items} selected={selected} onNavigate={go} />
          </Sider>
        )}
        {isMobile && (
          <Drawer
            placement="left"
            width={280}
            open={navOpen}
            onClose={() => setNavOpen(false)}
            closable={false}
            title={null}
            styles={{ header: { display: 'none' }, body: { padding: 0, background: SIDER_BG } }}
          >
            <SiderNav me={me} collapsed={false} items={items} selected={selected} onNavigate={go} />
          </Drawer>
        )}
        <Layout className="h-[100dvh] min-w-0">
          <Header
            className={`flex shrink-0 items-center border-b border-gray-100 !pl-0 shadow-sm ${
              isMobile ? '!h-14 !pr-3 !leading-[56px]' : '!pr-4'
            }`}
          >
            <NavToggle
              isMobile={isMobile}
              collapsed={collapsed}
              onClick={isMobile ? () => setNavOpen(true) : toggleCollapsed}
            />
            <div className="flex min-w-0 flex-1 items-center">
              <div ref={setSlotEl} className="contents" />
              {!slotActive && (
                <Typography.Text strong className="truncate text-base">
                  {headerTitle(items, selected)}
                </Typography.Text>
              )}
            </div>
          </Header>
          <Content id="app-content" className="min-h-0 flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-7">
            <ConfigProvider theme={isMobile ? MOBILE_THEME : undefined}>
              <div className="mx-auto max-w-[1320px]">{children}</div>
            </ConfigProvider>
          </Content>
        </Layout>
      </Layout>
    </HeaderSlotContext.Provider>
  );
}
